import React, { useState } from 'react'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import {
  Area, Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import GroupsIcon from '@mui/icons-material/Groups'
import StorefrontIcon from '@mui/icons-material/Storefront'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import PercentIcon from '@mui/icons-material/Percent'
import { useGetSmeDashboardQuery } from '../../redux/growthosSmeApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_PAGE_SX, GX_POSITIVE, GX_NEGATIVE, GxSyncStatusBar, GxPill, GX_COLORS,
} from '../growthos-shared/GrowthOsUi'
import { GROUPED_METRIC_GROUPS, GxPagedTable, periodLeadGroup, groupedMetricColumns } from '../growthos-shared/GxPagedTable'
import {
  integer, currency, compactCurrency, percentage, comparison, monthLabel, shortDate, daysAgoLabel,
} from '../growthos-shared/gxFormat'
import { KpiTile, Stat, ScopeTag, SectionTitle, TileGrid, TaperedFunnel, InsightPanel, Panel } from './SmeParityUi'

const INK_400 = '#94a3b8'

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]

const POC_TITLE = "The account's Sales POC — deal owner on HubSpot tables; the Data → Brands assignment on ProShip tables."
const LAST_SHIP_TITLE = "The brand's most recent positive shipment, measured against the ProShip data-through date."
const OWNER_TITLE = 'Raw HubSpot owner — may not be a classified Sales POC.'

function BrandTable({ brands, extraCol, timeframe }) {
  const trailCount = extraCol === 'newActive' ? 3 : 1
  return (
    <GxPagedTable variant="sme"
      rows={brands || []}
      rowKey={(b) => b.brandId}
      emptyText="No brands."
      headerGroups={[periodLeadGroup(2, timeframe), ...GROUPED_METRIC_GROUPS, { label: '', span: trailCount }]}
      columns={[
        { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <Box component="span" sx={{ fontWeight: 600 }}>{b.brandName}</Box> },
        { key: 'poc', header: 'Sales POC', title: POC_TITLE, sortValue: (b) => b.poc, cell: (b) => b.poc },
        ...groupedMetricColumns((b) => b),
        { key: 'lastShip', header: 'Last shipment', title: LAST_SHIP_TITLE, align: 'right', sortValue: (b) => b.daysSinceShipment ?? -1, cell: (b) => daysAgoLabel(b.daysSinceShipment) },
        ...(extraCol === 'newActive' ? [
          { key: 'dealClosed', header: 'Deal Closed date', title: "Close date of the brand's won deal (HubSpot)", sortValue: (b) => b.dealClosedDate ?? '', cell: (b) => (b.dealClosedDate ? shortDate(b.dealClosedDate) : '—') },
          { key: 'firstShip', header: 'First shipment', title: 'First-ever positive ProShip shipment', sortValue: (b) => b.firstShipmentAt ?? '', cell: (b) => (b.firstShipmentAt ? shortDate(b.firstShipmentAt) : '—') },
        ] : []),
      ]}
    />
  )
}

function DealDrill({ deals, note }) {
  return (
    <Box>
      {note && <Typography sx={{ mb: 1, fontSize: 12, color: GX_INK_500 }}>{note}</Typography>}
      <GxPagedTable variant="sme"
        rows={deals || []}
        rowKey={(d) => d.dealId}
        emptyText="No deals."
        columns={[
          { key: 'deal', header: 'Brand / Deal', sortValue: (d) => d.brandName, cell: (d) => <Box component="span" sx={{ fontWeight: 600 }}>{d.brandName}</Box> },
          { key: 'owner', header: 'Owner', title: OWNER_TITLE, sortValue: (d) => d.owner, cell: (d) => d.owner },
          { key: 'status', header: 'Status', title: 'Deal outcome: Won, Lost, or Open (undecided).', sortValue: (d) => d.status, cell: (d) => <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, fontSize: 11, fontWeight: 600 }}>{d.status}</Box> },
          { key: 'stage', header: 'Stage', title: 'Current HubSpot pipeline stage.', sortValue: (d) => d.stage ?? '', cell: (d) => d.stage ?? '—' },
          { key: 'created', header: 'Created', title: 'Deal create date (HubSpot).', sortValue: (d) => d.createdDate ?? '', cell: (d) => d.createdDate ?? '—' },
          { key: 'closed', header: 'Closed', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => d.closedDate ?? '—' },
          { key: 'shipPot', header: 'Shipment Potential', title: 'Expected monthly shipments (orders/month) on the deal.', align: 'right', sortValue: (d) => d.shipmentPotential, cell: (d) => integer(d.shipmentPotential) },
          { key: 'mbr', header: 'Est. MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', align: 'right', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) },
        ]}
      />
    </Box>
  )
}

function WipDrill({ deals }) {
  return (
    <Box>
      <Typography sx={{ mb: 1, fontSize: 12, color: GX_INK_500 }}>Open Hot/Warm deals owned by SME POCs, across all HubSpot pipelines — a live snapshot.</Typography>
      <GxPagedTable variant="sme"
        rows={deals || []}
        rowKey={(d) => d.dealId}
        emptyText="No open Hot/Warm deals."
        columns={[
          { key: 'deal', header: 'Brand / Deal', sortValue: (d) => d.brandName, cell: (d) => <Box component="span" sx={{ fontWeight: 600 }}>{d.brandName}</Box> },
          { key: 'owner', header: 'Owner', title: OWNER_TITLE, sortValue: (d) => d.owner, cell: (d) => d.owner },
        ]}
      />
    </Box>
  )
}

const growthStr = (p) => (p == null ? '—' : `${p >= 0 ? '↑' : '↓'} ${Math.abs(p).toFixed(1)}%`)
const growthColor = (p) => (p == null ? INK_400 : p > 0 ? GX_POSITIVE : p < 0 ? GX_NEGATIVE : INK_400)

/** Month graph keys on revenue+shipments; POC graph on shipments only (reference moodFor). */
function moodFor(shipOnly, rev, ship) {
  const r = rev ?? 0; const s = ship ?? 0
  const positive = shipOnly ? s > 0 : (r > 0 && s > 0)
  const negative = shipOnly ? s < 0 : (r < 0 && s < 0)
  if (positive) return { label: shipOnly ? 'Growing' : 'Growth Month', face: 'happy', color: GX_POSITIVE, bg: 'rgba(5,150,105,0.12)' }
  if (negative) return { label: shipOnly ? 'Declining' : 'Decline Month', face: 'sad', color: GX_NEGATIVE, bg: 'rgba(225,29,72,0.12)' }
  return { label: shipOnly ? 'Stable' : 'Stable Month', face: 'neutral', color: INK_400, bg: 'rgba(148,163,184,0.14)' }
}

const tipBox = { background: '#fff', border: `1px solid ${GX_BORDER}`, borderRadius: 10, padding: '8px 10px', fontSize: 12, color: GX_INK_700, boxShadow: '0 6px 20px rgba(20,28,54,.12)' }
function TrendTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600 }}>{monthLabel(d.month)}{d.comparable ? ' · MTD' : ''}</div>
      <div>Shipments: {integer(d.shipments)}</div>
      <div>Revenue: {currency(d.revenue)}</div>
      <div>Rev growth: <span style={{ color: growthColor(d.revenueGrowthPct) }}>{growthStr(d.revenueGrowthPct)}</span></div>
      <div>Ship growth: <span style={{ color: growthColor(d.shipmentGrowthPct) }}>{growthStr(d.shipmentGrowthPct)}</span></div>
    </div>
  )
}
function PocTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600 }}>{d.owner}</div>
      <div>Revenue: {currency(d.revenue)} <span style={{ color: INK_400 }}>(prev {currency(d.prevRevenue)})</span></div>
      <div>Shipments: {integer(d.shipments)} <span style={{ color: INK_400 }}>(prev {integer(d.prevShipments)})</span></div>
      <div>Rev growth: <span style={{ color: growthColor(d.revenueGrowthPct) }}>{growthStr(d.revenueGrowthPct)}</span></div>
      <div>Ship growth: <span style={{ color: growthColor(d.shipmentGrowthPct) }}>{growthStr(d.shipmentGrowthPct)}</span></div>
    </div>
  )
}
/** POC chart x-axis tick: medal for the top 3, first name only. */
function PocTick({ x, y, payload }) {
  const medals = ['🥇', '🥈', '🥉']
  const rank = payload.index
  const name = String(payload.value).split(' ')[0]
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={13} textAnchor="middle" fontSize={10} fill={GX_INK_500}>{rank < 3 ? `${medals[rank]} ` : ''}{name}</text>
    </g>
  )
}

function Collapsible({ title, icon, subtitle, open, onToggle, children }) {
  return (
    <Panel sx={{ mb: 3 }}>
      <Box component="button" type="button" onClick={onToggle} aria-expanded={open}
        sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 1, textAlign: 'left', border: 'none', bgcolor: 'transparent', cursor: 'pointer', font: 'inherit' }}>
        <Box>
          <SectionTitle icon={icon}>{title}</SectionTitle>
          <Typography sx={{ fontSize: 12, color: INK_400, mt: -0.75 }}>{subtitle}</Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 22, color: INK_400, transition: 'transform 160ms', transform: open ? 'rotate(180deg)' : 'none' }} />
      </Box>
      {open && <Box sx={{ px: 2.5, pb: 2.5 }}>{children}</Box>}
    </Panel>
  )
}

const DetailBox = ({ children }) => <Panel sx={{ p: 1.5, mt: 1.5 }}>{children}</Panel>

export default function SmePerformancePage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [open, setOpen] = useState(null)
  const toggle = (k) => setOpen((cur) => (cur === k ? null : k))
  const [pocOpen, setPocOpen] = useState(false)
  const [topOpen, setTopOpen] = useState(false)
  const [hoverMonth, setHoverMonth] = useState(null)
  const [hoverPoc, setHoverPoc] = useState(null)
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, isFetching, error } = useGetSmeDashboardQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const loading = isFetching && !data
  const proshipLabel = data?.coverage?.proship?.label
  const hubspotLabel = data?.coverage?.hubspot?.label
  const dataThrough = data?.coverage?.proship?.dataThrough

  const trendData = (data?.trend || []).map((t) => ({ ...t, label: monthLabel(t.month) }))
  const pctOf = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0)
  const funnelStages = data ? [
    { key: 'opps', label: 'Opportunities', count: data.funnel.opportunities, pct: 100, icon: <GpsFixedIcon sx={{ fontSize: 17 }} />, gradient: 'linear-gradient(135deg,#4F46E5,#818CF8)', shadow: 'rgba(79,70,229,.32)' },
    { key: 'decided', label: 'Decided (Won + Lost)', count: data.funnel.decided, pct: pctOf(data.funnel.decided, data.funnel.opportunities), icon: <FilterAltIcon sx={{ fontSize: 17 }} />, gradient: 'linear-gradient(135deg,#0D9488,#2DD4BF)', shadow: 'rgba(13,148,136,.32)' },
    // Customers · New (created AND won-closed in window), not the Deal Pipeline's Customers · Closed.
    { key: 'won', label: 'Customers · New', count: data.funnel.won, pct: pctOf(data.funnel.won, data.funnel.opportunities), icon: <EmojiEventsIcon sx={{ fontSize: 17 }} />, gradient: 'linear-gradient(135deg,#D97706,#FBBF24)', shadow: 'rgba(217,119,6,.32)' },
  ] : []
  const trend = data?.trend || []
  const activeMonth = trend.length ? trend[hoverMonth ?? trend.length - 1] : null
  const pocPerf = data?.pocPerformance || []
  const activePoc = pocPerf.length ? pocPerf[Math.min(hoverPoc ?? 0, pocPerf.length - 1)] : null

  const detailPanel = (key) => {
    if (open !== key || !data) return null
    const map = {
      growing: <BrandTable brands={data.brandSnapshot.growing} timeframe={proshipLabel} />,
      declining: <BrandTable brands={data.brandSnapshot.declining} timeframe={proshipLabel} />,
      churned: <BrandTable brands={data.brandSnapshot.churned} timeframe={proshipLabel} />,
      newBrands: <BrandTable brands={data.newAndWon.newActiveBrandList} extraCol="newActive" timeframe={proshipLabel} />,
      dealsWon: (
        <GxPagedTable variant="sme"
          rows={data.newAndWon.wonDeals || []}
          rowKey={(d) => d.dealId}
          emptyText="No won deals in this window."
          columns={[
            { key: 'deal', header: 'Deal / Brand', title: 'HubSpot deal, labelled by its linked brand.', sortValue: (d) => d.brandName, cell: (d) => <Box component="span" sx={{ fontWeight: 600 }}>{d.brandName}</Box> },
            { key: 'owner', header: 'Owner', title: OWNER_TITLE, sortValue: (d) => d.owner, cell: (d) => d.owner },
            { key: 'closed', header: 'Closed', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => d.closedDate ?? '—' },
            { key: 'shipPot', header: 'Shipment Potential', title: 'Expected monthly shipments (orders/month) on the deal.', align: 'right', sortValue: (d) => d.shipmentPotential, cell: (d) => integer(d.shipmentPotential) },
            { key: 'mbr', header: 'Est. MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', align: 'right', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) },
          ]}
        />
      ),
      created: <DealDrill deals={data.dealPipeline.createdDeals} note="SME-POC-owned deals created in this window (all HubSpot pipelines)." />,
      wip: <WipDrill deals={data.dealPipeline.wipDeals} />,
      won: <DealDrill deals={data.dealPipeline.wonDeals} note="SME-POC-owned deals reaching Won/Onboarded with a close date in this window (= Customers · Closed)." />,
      lost: <DealDrill deals={data.dealPipeline.lostDeals} note="SME-POC-owned deals marked Lost with a close date in this window." />,
      winRate: (
        <Typography sx={{ fontSize: 13, color: '#475569', p: 0.5 }}>
          <strong>Win Rate = Won ÷ (Won + Lost)</strong>, over deals decided (closed) in this window — bounded ≤100%.
          {' '}This window: <strong>{integer(data.dealPipeline.dealsWon)}</strong> won ÷ ({integer(data.dealPipeline.dealsWon)} won + {integer(data.dealPipeline.dealsLost)} lost) = <strong>{percentage(data.dealPipeline.winRate)}</strong>. Same definition as the Sales POC dashboard.
        </Typography>
      ),
      customersNew: <DealDrill deals={data.sales.customersNewDeals} note="Deals opened AND won inside this window — wins from work started this period." />,
      customersClosed: <DealDrill deals={data.sales.customersClosedDeals} note="Deals reaching Won/Onboarded with a close date in this window, regardless of when created." />,
      reactivated: <BrandTable brands={data.retention.reactivated} timeframe={proshipLabel} />,
    }
    return <DetailBox>{map[key]}</DetailBox>
  }

  const t = data?.targets
  const revVal = data?.summary.revenue.value ?? 0
  const shipVal = data?.summary.shipments.value ?? 0

  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <GxSyncStatusBar runs={runsData?.runs || []} />
        {dataThrough && <Box sx={{ mb: 2 }}><GxPill color={GX_COLORS.indigo}>ProShip data through: {shortDate(dataThrough)}</GxPill></Box>}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#4f46e5' }}>
            <WorkOutlineIcon sx={{ fontSize: 15 }} /> SME-segment brands · all pipelines
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>SME Performance</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 820 }}>
            Scoped by SME <strong>Sales POC</strong> tagging (Sales POCs page). <strong>Deals</strong> = deals owned by an SME-tagged POC across all HubSpot pipelines.
            {' '}<strong>Brands</strong> = tagged SME <em>and</em> assigned to an SME POC; their revenue &amp; shipments are ProShip actuals (capped at the ProShip data-through date).
            {data ? ` ${data.smePocCount} SME POC${data.smePocCount === 1 ? '' : 's'} with assigned brands.` : ''} Empty until you tag Sales POCs as SME.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => { setPeriod(e.target.value); setOpen(null) }} sx={{ minWidth: 140, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: INK_400 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {data?.window && <ScopeTag label={`Showing: ${shortDate(data.window.from)} – ${shortDate(data.window.to)}`} />}
        </Box>
      </Box>

      {error && (
        <Box sx={{ borderRadius: '16px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', p: 2, fontSize: 13, color: '#be123c', mb: 2 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {period === 'custom' && !customReady && (
        <Box sx={{ borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', p: 2, fontSize: 13, color: '#b45309', mb: 2 }}>Pick a start and end date to load the custom range.</Box>
      )}
      {data && !data.hasData && (
        <Box sx={{ borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', p: 2, fontSize: 13, color: '#b45309', mb: 2 }}>No SME-segment activity in this window. Classify brands as SME on the Brands page and tag SME Sales POCs.</Box>
      )}

      {/* Team performance */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<GroupsIcon sx={{ fontSize: 17 }} />} tag={proshipLabel && <ScopeTag label={`ProShip: ${proshipLabel}`} />}>Team Performance</SectionTitle>
        <TileGrid cols={5}>
          <KpiTile label="SME Shipments" accent="indigo" icon={<Inventory2Icon sx={{ fontSize: 16 }} />} loading={loading}
            value={data ? integer(data.summary.shipments.value) : undefined} sub={data ? comparison(data.summary.shipments, integer) : undefined}
            definition="Σ ProShip shipments in the window for qualifying SME brands (tagged SME AND assigned to an SME Sales POC). Capped at the ProShip data-through date. Excluded brands dropped; hidden brands still counted here." />
          <KpiTile label="SME Revenue" accent="teal" icon={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />} loading={loading}
            value={data ? currency(data.summary.revenue.value) : undefined} sub={data ? comparison(data.summary.revenue, currency) : undefined}
            definition="Σ ProShip revenue (₹) in the window for qualifying SME brands. Same scope/capping as SME Shipments." />
          <KpiTile label="Active Brands" accent="amber" icon={<StorefrontIcon sx={{ fontSize: 16 }} />} loading={loading}
            value={data ? integer(data.summary.activeBrands.value) : undefined} sub={data ? comparison(data.summary.activeBrands, integer) : undefined}
            definition="Distinct qualifying SME brands with at least one positive shipment in the window." />
          <KpiTile label="Revenue Share" accent="rose" icon={<PercentIcon sx={{ fontSize: 16 }} />} loading={loading}
            value={data ? percentage(data.summary.revenueSharePct) : undefined} sub="of total ProShip revenue"
            definition="Qualifying-SME-brand revenue (segment = SME AND assigned Sales POC tagged SME) ÷ all-brand ProShip revenue in the window × 100 — the SME team's share of the total book." />
          <KpiTile label="Avg Revenue / Brand" accent="indigo" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} loading={loading}
            value={data ? currency(data.summary.avgRevenuePerBrand) : undefined} sub="SME revenue ÷ active brands"
            definition="SME revenue in the window ÷ SME brands with a positive shipment in the window." />
        </TileGrid>
      </Box>

      {/* SME brand snapshot */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<StorefrontIcon sx={{ fontSize: 17 }} />}>SME Brand Snapshot</SectionTitle>
        <TileGrid cols={4}>
          <Stat label="Brands Growing" value={data ? integer(data.brandSnapshot.growingCount) : '—'} sub="Click to expand" accent="teal" icon={<TrendingUpIcon sx={{ fontSize: 14 }} />} chevron={false}
            active={open === 'growing'} onClick={() => toggle('growing')}
            definition="SME brands whose window revenue is higher than the equivalent prior window (both windows positive). Count includes hidden brands; the table lists only visible ones." />
          <Stat label="Brands Declining" value={data ? integer(data.brandSnapshot.decliningCount) : '—'} sub="Click to expand" accent="amber" icon={<TrendingDownIcon sx={{ fontSize: 14 }} />} chevron={false}
            active={open === 'declining'} onClick={() => toggle('declining')}
            definition="SME brands whose window revenue is below the equivalent prior window (includes brands that dropped to zero)." />
          <Stat label="Churned Brands" value={data ? integer(data.brandSnapshot.churnedCount) : '—'} sub="No shipment in 30d · click" accent="rose" icon={<RemoveShoppingCartIcon sx={{ fontSize: 14 }} />} chevron={false}
            active={open === 'churned'} onClick={() => toggle('churned')}
            definition="SME brands with no positive shipment in the 30 days ending at the ProShip data-through date. This is a current-state metric, so it does not change with the period filter." />
        </TileGrid>
        {['growing', 'declining', 'churned'].includes(open) && detailPanel(open)}
      </Box>

      {/* New active brands & deals won */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />} tag={hubspotLabel && <ScopeTag label={`HubSpot: ${hubspotLabel}`} />}>New Active Brands &amp; Deals Won</SectionTitle>
        <TileGrid cols={4}>
          <Stat label="New Active Brands" value={data ? integer(data.newAndWon.newActiveBrands) : '—'} sub="1st shipment in window · click" accent="indigo" icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />} chevron={false}
            active={open === 'newBrands'} onClick={() => toggle('newBrands')}
            definition="Qualifying SME brands whose first-ever positive ProShip shipment falls in the window — brand-side (ProShip), not deals." />
          <Stat label="Deals Won" value={data ? integer(data.newAndWon.dealsWon) : '—'} sub="Closed-won in window · click" accent="teal" icon={<EmojiEventsIcon sx={{ fontSize: 14 }} />} chevron={false}
            active={open === 'dealsWon'} onClick={() => toggle('dealsWon')}
            definition="Deals owned by an SME-tagged Sales POC (all HubSpot pipelines) that reached Won/Onboarded with a close date in the window (= Customers · Closed)." />
          <Stat label="Shipment Potential" value={data ? integer(data.newAndWon.shipmentPotential) : '—'} sub="won deals’ monthly orders" accent="amber"
            definition="Σ expected monthly orders (shipment potential) of the deals WON in this window — a HubSpot deal-side forward estimate, not ProShip actuals." />
          <Stat label="Actual Shipments" value={data ? integer(data.newAndWon.actualShipments) : '—'} sub="new brands this window" accent="emerald"
            definition="Σ actual ProShip shipments in this window from the New Active Brands (brand-side actuals). Distinct cohort from the won-deals Shipment Potential beside it." />
        </TileGrid>
        {['newBrands', 'dealsWon'].includes(open) && detailPanel(open)}
      </Box>

      {/* Deal pipeline */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<FilterAltIcon sx={{ fontSize: 17 }} />} tag={hubspotLabel && <ScopeTag label={`HubSpot: ${hubspotLabel}`} />}>Deal Pipeline</SectionTitle>
        <TileGrid cols={6} smCols={3} xsCols={2}>
          <Stat label="Created" value={data ? integer(data.dealPipeline.dealsCreated) : '—'} accent="indigo" active={open === 'created'} onClick={() => toggle('created')}
            definition="Deals owned by an SME-tagged Sales POC, created in this window (all HubSpot pipelines). Click for the deal list." />
          <Stat label="WIP · Live" value={data ? integer(data.dealPipeline.wip) : '—'} accent="teal" active={open === 'wip'} onClick={() => toggle('wip')}
            definition="Work In Progress — SME-POC-owned open deals that are Hot or Warm (matches the 'SME Hot & Warm Open' HubSpot view) — a live snapshot, any creation date, not window-scoped. Cold/Hibernating/unclassified open deals are excluded. Click for the deal list." />
          <Stat label="Customers · Closed" value={data ? integer(data.dealPipeline.dealsWon) : '—'} accent="emerald" active={open === 'won'} onClick={() => toggle('won')}
            definition="SME-POC-owned deals reaching Won/Onboarded with a close date in this window (any create date). Click for the deal list." />
          <Stat label="Lost" value={data ? integer(data.dealPipeline.dealsLost) : '—'} accent="rose" active={open === 'lost'} onClick={() => toggle('lost')}
            definition="SME-POC-owned deals marked Lost with a close date in this window. Click for the deal list." />
          <Stat label="Open Pipeline" value={data ? integer(data.sales.openPipelineValue) : '—'} sub="shipments/month potential · live"
            definition="Σ shipment potential (expected monthly shipments) of ALL currently-open SME deals, any creation date — the standing backlog the team is working." />
          <Stat label="Win Rate" value={data ? percentage(data.dealPipeline.winRate) : '—'} accent="amber" active={open === 'winRate'} onClick={() => toggle('winRate')}
            definition="Won ÷ decided (won + lost) deals closed in this window × 100 — bounded to ≤100%. Same definition as the Sales POC dashboard. Click for the breakdown." />
        </TileGrid>
        {['created', 'wip', 'won', 'lost', 'winRate'].includes(open) && detailPanel(open)}
      </Box>

      {/* SME sales funnel + sales motion */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<GpsFixedIcon sx={{ fontSize: 17 }} />} tag={hubspotLabel && <ScopeTag label={`HubSpot: ${hubspotLabel}`} />}>SME Sales Funnel</SectionTitle>
        <Panel sx={{ p: 2.5 }}>
          {data && funnelStages.length > 0 && <TaperedFunnel stages={funnelStages} minWidthPct={40} />}
          <Typography sx={{ mt: 1, fontSize: 12, color: INK_400 }}>
            Created-cohort funnel: of deals opened this window, how many reached a decision and were won. Opportunity → Won conversion: <strong>{data ? percentage(data.funnel.oppToWonPct) : '—'}</strong>. Recent windows read low — deals opened late haven’t had time to close.
          </Typography>
        </Panel>
        <TileGrid cols={5} smCols={3} xsCols={2} sx={{ mt: 2 }}>
          <Stat label="Customers · Closed" value={data ? integer(data.dealPipeline.customersClosed) : '—'} sub="won & closed in-window" active={open === 'customersClosed'} onClick={() => toggle('customersClosed')}
            definition="Deals reaching Won/Onboarded with a close date in this window, regardless of when they were created (= Deals Won). Click for the deal list." />
          <Stat label="Customers · New" value={data ? integer(data.dealPipeline.customersNew) : '—'} sub="opened & won in-window" active={open === 'customersNew'} onClick={() => toggle('customersNew')}
            definition="Deals opened AND won inside this window — wins from work started this period (vs older deals closing now). Click for the deal list." />
          <Stat label="Opp → Won %" value={data ? percentage(data.funnel.oppToWonPct) : '—'} sub="of deals opened"
            definition="Of deals created in the window, the share won so far. A cohort conversion rate — recent periods read low." />
          <Stat label="Avg Sales Cycle" value={data && data.sales.avgSalesCycleDays != null ? `${data.sales.avgSalesCycleDays}d` : '—'} sub="opened → won"
            definition="Average days from a deal opening to being won, over deals won in this window." />
          <Stat label="Avg Deal Size" value={data ? integer(data.sales.avgDealSize) : '—'} sub="monthly orders / win"
            definition="Expected monthly orders per won deal (Σ shipment potential ÷ won deals)." />
        </TileGrid>
        {['customersNew', 'customersClosed'].includes(open) && detailPanel(open)}
      </Box>

      {/* Revenue & unit economics */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<CurrencyRupeeIcon sx={{ fontSize: 17 }} />} tag={proshipLabel && <ScopeTag label={`ProShip: ${proshipLabel}`} />}>Revenue &amp; Unit Economics</SectionTitle>
        <TileGrid cols={5} smCols={3} xsCols={2}>
          <Stat label="Revenue Growth" value={data ? percentage(data.revenueMix.momRevenueGrowthPct) : '—'} sub="vs comparable prior period"
            definition="This window's SME revenue vs the equivalent prior window (same day-range last period) — follows the period filter. Same basis as the Team Performance card." />
          <Stat label="Shipment Growth" value={data ? percentage(data.revenueMix.momShipmentGrowthPct) : '—'} sub="vs comparable prior period"
            definition="This window's SME shipments vs the equivalent prior window (same day-range last period) — follows the period filter." />
          <Stat label="New-Brand Revenue" value={data ? currency(data.revenueMix.newBrandRevenue) : '—'} sub={data ? `vs ${currency(data.revenueMix.existingBrandRevenue)} existing` : 'this window'}
            definition="Revenue from brands whose first-ever shipment is in this window, vs established brands — the quality of growth." />
          <Stat label="Top-5 Concentration" value={data ? percentage(data.revenueMix.concentrationTop5Pct) : '—'} sub="of SME revenue"
            definition="Share of SME revenue from the top 5 brands — a risk gauge for over-reliance on a few accounts." />
          <Stat label="Revenue / Shipment" value={data ? currency(data.revenueMix.revenuePerShipment) : '—'} sub="avg ₹ per parcel"
            definition="SME revenue ÷ SME shipments — basic unit economics and a mix-shift signal." />
        </TileGrid>
      </Box>

      {/* Shipment performance + retention */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: open === 'reactivated' ? 0 : 3 }}>
        <Box>
          <SectionTitle icon={<TrendingUpIcon sx={{ fontSize: 17 }} />}>Shipment Performance</SectionTitle>
          <TileGrid cols={3} smCols={3} xsCols={3}>
            <Stat label="Max SOW (prorated)" value={data ? integer(data.shipmentPerf.maxSow) : '—'} accent="indigo" sub="wallet ceiling · window-adjusted"
              definition="Share Of Wallet ceiling — Σ over active SME brands WITH a SOW value (KAM/SAM manual entry, else HubSpot forward-orders/month) — PRORATED from the monthly rate to this window's day-count. A short window shows a proportionally smaller ceiling." />
            <Stat label="Achieved" value={data ? integer(data.shipmentPerf.achievedShipments) : '—'} accent="emerald" sub={data ? `of ${integer(data.shipmentPerf.totalShipments)} total · SOW-covered brands` : 'actual shipments'}
              definition="Window shipments of the SOW-covered brands only (the same brands in the ceiling), so Achievement compares like-for-like. Total SME shipments incl. brands without a SOW value are shown in Team Performance." />
            <Stat label="Achievement" value={data ? percentage(data.shipmentPerf.shipmentAchievementPct) : '—'} accent="amber" sub="achieved ÷ Max SOW"
              definition="Achieved (SOW-covered brands) ÷ prorated Max SOW × 100 — how much of the addressable wallet the SME team is capturing." />
          </TileGrid>
        </Box>
        <Box>
          <SectionTitle icon={<FavoriteBorderIcon sx={{ fontSize: 17 }} />}>Retention</SectionTitle>
          <TileGrid cols={2} smCols={2} xsCols={2}>
            <Stat label="Retention Rate" value={data ? percentage(data.retention.retentionRatePct) : '—'} accent="teal" sub={data ? `${integer(data.retention.activeBoth)}/${integer(data.retention.activePrior)} brands` : 'kept active'}
              definition="Share of last period's active SME brands still active this period." />
            <Stat label="Reactivated" value={data ? integer(data.retention.reactivatedBrands) : '—'} accent="emerald" sub="came back this window" active={open === 'reactivated'} onClick={() => toggle('reactivated')}
              definition="Brands that had gone quiet (inactive last period) and shipped again this period. Click for the brand list." />
          </TileGrid>
        </Box>
      </Box>
      {open === 'reactivated' && <Box sx={{ mb: 3 }}>{detailPanel('reactivated')}</Box>}

      {/* Target vs actual (team) — only when targets exist */}
      {data && t && t.revenueTarget != null && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle icon={<GpsFixedIcon sx={{ fontSize: 17 }} />}>Target vs Actual · Team</SectionTitle>
          <TileGrid cols={4} smCols={2}>
            <Stat label="Revenue Attainment" value={percentage(t.revenueAttainmentPct)} sub={`${currency(revVal)} of ${currency(t.revenueTarget)} target`}
              definition="SME revenue this window vs the monthly revenue targets of SME POCs (set on the Sales POCs page), PRORATED to the days this window covers — a mid-month view is judged against a mid-month target." />
            <Stat label="Revenue Gap" value={currency(Math.max(0, (t.revenueTarget ?? 0) - revVal))} accent={revVal >= t.revenueTarget ? 'emerald' : 'amber'} sub="remaining to prorated target"
              definition="Prorated target − actual revenue (₹0 when the target is met)." />
            <Stat label="Shipment Attainment" value={percentage(t.shipmentAttainmentPct)} sub={t.shipmentsTarget != null ? `${integer(shipVal)} of ${integer(t.shipmentsTarget)} target` : 'no shipment target set'}
              definition="SME shipments vs the prorated monthly shipment targets of SME POCs." />
            <Stat label="Shipment Gap" value={t.shipmentsTarget != null ? integer(Math.max(0, (t.shipmentsTarget ?? 0) - shipVal)) : '—'} accent={t.shipmentsTarget != null && shipVal >= t.shipmentsTarget ? 'emerald' : 'amber'} sub="remaining to prorated target"
              definition="Prorated target − actual shipments (0 when the target is met)." />
          </TileGrid>
        </Box>
      )}

      {/* Graph 1 — Monthly SME Revenue & Shipments */}
      <Panel sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Monthly SME Revenue &amp; Shipments</Typography>
            <Typography sx={{ fontSize: 12, color: INK_400, maxWidth: 640 }}>12-month SME-only trend with revenue, shipments, and month-over-month movement. The current month compares against the previous month’s equal to-date range.</Typography>
          </Box>
          {activeMonth && (
            <InsightPanel title={`${monthLabel(activeMonth.month)}${activeMonth.comparable ? ' · MTD' : ''}`} mood={moodFor(false, activeMonth.revenueGrowthPct, activeMonth.shipmentGrowthPct)} rows={[
              { label: 'Revenue', value: currency(activeMonth.revenue) },
              { label: 'Shipments', value: integer(activeMonth.shipments) },
              { label: 'Revenue growth', value: growthStr(activeMonth.revenueGrowthPct), color: growthColor(activeMonth.revenueGrowthPct) },
              { label: 'Shipment growth', value: growthStr(activeMonth.shipmentGrowthPct), color: growthColor(activeMonth.shipmentGrowthPct) },
            ]} />
          )}
        </Box>
        <Box sx={{ mt: 1.5, height: 288 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trendData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
              onMouseMove={(s) => setHoverMonth(typeof s?.activeTooltipIndex === 'number' ? s.activeTooltipIndex : null)} onMouseLeave={() => setHoverMonth(null)}>
              <defs>
                <linearGradient id="smeShipBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.45} />
                </linearGradient>
                <linearGradient id="smeRevArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: GX_INK_500 }} axisLine={{ stroke: GX_BORDER }} tickLine={false} />
              <YAxis yAxisId="s" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => integer(v)} axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} axisLine={false} tickLine={false} />
              <RTooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(6,182,212,.06)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="s" dataKey="shipments" name="Shipments" fill="url(#smeShipBar)" radius={[5, 5, 0, 0]} maxBarSize={38} />
              <Area yAxisId="r" dataKey="revenue" name="Revenue" stroke="none" fill="url(#smeRevArea)" legendType="none" tooltipType="none" />
              <Line yAxisId="r" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Panel>

      {/* Graph 2 — Performance by Sales POC */}
      <Panel sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Performance by Sales POC</Typography>
            <Typography sx={{ fontSize: 12, color: INK_400, maxWidth: 640 }}>SME roster comparison — revenue and shipments for the selected period, with growth against the equal-length preceding period. Mood tracks shipment growth.</Typography>
          </Box>
          {activePoc && (
            <InsightPanel title={activePoc.owner} mood={moodFor(true, activePoc.revenueGrowthPct, activePoc.shipmentGrowthPct)} rows={[
              { label: 'Revenue', value: currency(activePoc.revenue) },
              { label: 'Shipments', value: integer(activePoc.shipments) },
              { label: 'Prev revenue', value: currency(activePoc.prevRevenue) },
              { label: 'Prev shipments', value: integer(activePoc.prevShipments) },
              { label: 'Revenue growth', value: growthStr(activePoc.revenueGrowthPct), color: growthColor(activePoc.revenueGrowthPct) },
              { label: 'Shipment growth', value: growthStr(activePoc.shipmentGrowthPct), color: growthColor(activePoc.shipmentGrowthPct) },
            ]} />
          )}
        </Box>
        <Box sx={{ mt: 1.5, height: 288, position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pocPerf} margin={{ top: 4, right: 8, left: 8, bottom: 18 }} barGap={4}
              onMouseMove={(s) => setHoverPoc(typeof s?.activeTooltipIndex === 'number' ? s.activeTooltipIndex : null)} onMouseLeave={() => setHoverPoc(null)}>
              <defs>
                <linearGradient id="smePocRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#818CF8" stopOpacity={0.5} />
                </linearGradient>
                <linearGradient id="smePocShip" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#6EE7B7" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
              <XAxis dataKey="owner" interval={0} height={28} tick={<PocTick />} axisLine={{ stroke: GX_BORDER }} tickLine={false} />
              <YAxis yAxisId="r" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} axisLine={false} tickLine={false} />
              <YAxis yAxisId="s" orientation="right" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => integer(v)} axisLine={false} tickLine={false} />
              <RTooltip content={<PocTooltip />} cursor={{ fill: 'rgba(79,70,229,.06)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="r" dataKey="revenue" name="Revenue" fill="url(#smePocRev)" radius={[5, 5, 0, 0]} maxBarSize={30} />
              <Bar yAxisId="s" dataKey="shipments" name="Shipments" fill="url(#smePocShip)" radius={[5, 5, 0, 0]} maxBarSize={30} />
            </BarChart>
          </ResponsiveContainer>
          {pocPerf.length === 0 && !isFetching && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: INK_400 }}>No SME POC activity.</Box>
          )}
        </Box>
      </Panel>

      {/* SME POC snapshot */}
      <Collapsible title="SME POC Snapshot" icon={<GroupsIcon sx={{ fontSize: 17 }} />} open={pocOpen} onToggle={() => setPocOpen((v) => !v)}
        subtitle={`Per-rep revenue, shipments (with growth) and deal outcomes · ${(data?.pocSnapshot || []).length} POCs`}>
        <GxPagedTable variant="sme"
          rows={data?.pocSnapshot || []}
          rowKey={(r) => r.owner}
          emptyText="No POC activity in this window."
          headerGroups={[
            periodLeadGroup(2, proshipLabel),
            ...GROUPED_METRIC_GROUPS,
            { label: '', span: 2 },
            { label: 'Deals', span: 5, group: 'ship', title: 'Deal count.' },
            { label: 'Target Attainment (% of prorated goal)', span: 2, group: 'rev', title: "Actual vs this POC's monthly target (set on the Sales POCs page), prorated to the days this window covers." },
          ]}
          columns={[
            { key: 'poc', header: 'Sales POC', title: POC_TITLE, sortValue: (r) => r.owner, cell: (r) => <Box component="span" sx={{ fontWeight: 600 }}>{r.owner}</Box> },
            { key: 'activeBrands', header: 'Active Brands', title: 'Distinct brands with ≥1 positive shipment in the window.', align: 'right', sortValue: (r) => r.activeBrands, cell: (r) => integer(r.activeBrands) },
            ...groupedMetricColumns((r) => ({ prevShipments: r.prevShipments, shipments: r.shipments, shipmentChangePct: r.shipmentGrowthPct, prevRevenue: r.prevRevenue, revenue: r.revenue, revenueChangePct: r.revenueGrowthPct })),
            { key: 'lastShip', header: 'Last shipment', title: LAST_SHIP_TITLE, align: 'right', sortValue: (r) => r.daysSinceShipment ?? -1, cell: (r) => daysAgoLabel(r.daysSinceShipment) },
            { key: 'revPerBrand', header: 'Rev / Brand', title: 'Revenue ÷ active brands', align: 'right', sortValue: (r) => r.revenuePerBrand, cell: (r) => currency(r.revenuePerBrand) },
            { key: 'created', header: 'Created', title: 'Deals created in this window', align: 'right', group: 'ship', edge: 'left', sortValue: (r) => r.dealsCreated, cell: (r) => integer(r.dealsCreated) },
            { key: 'wip', header: 'WIP', title: 'Open Hot/Warm deals, all pipelines (live)', align: 'right', group: 'ship', sortValue: (r) => r.wip, cell: (r) => integer(r.wip) },
            { key: 'won', header: 'Won', align: 'right', group: 'ship', sortValue: (r) => r.dealsWon, cell: (r) => integer(r.dealsWon) },
            { key: 'lost', header: 'Lost', title: 'Deals marked Lost with a close date in the window.', align: 'right', group: 'ship', sortValue: (r) => r.dealsLost, cell: (r) => integer(r.dealsLost) },
            { key: 'winRate', header: 'Win %', align: 'right', group: 'ship', edge: 'right', sortValue: (r) => r.winRate ?? -1, cell: (r) => percentage(r.winRate) },
            { key: 'revAttain', header: 'Revenue', title: 'SME revenue this window ÷ summed monthly revenue target × 100', align: 'right', group: 'rev', edge: 'left', sortValue: (r) => r.revenueAttainmentPct ?? -1,
              cell: (r) => <span title={r.revenueTarget != null ? `Target ${currency(r.revenueTarget)}` : 'No target set'}>{r.revenueAttainmentPct != null ? percentage(r.revenueAttainmentPct) : '—'}</span> },
            { key: 'shipAttain', header: 'Shipments', title: 'SME shipments this window ÷ summed monthly shipment target × 100', align: 'right', group: 'rev', edge: 'right', sortValue: (r) => r.shipmentAttainmentPct ?? -1,
              cell: (r) => <span title={r.shipmentsTarget != null ? `Target ${integer(r.shipmentsTarget)}` : 'No target set'}>{r.shipmentAttainmentPct != null ? percentage(r.shipmentAttainmentPct) : '—'}</span> },
          ]}
        />
      </Collapsible>

      {/* Top SME brands */}
      <Collapsible title="Top SME Brands" icon={<EmojiEventsIcon sx={{ fontSize: 17 }} />} open={topOpen} onToggle={() => setTopOpen((v) => !v)}
        subtitle={`Top ${(data?.topBrands || []).length} by window revenue, vs the equivalent prior window · hidden-from-display brands are omitted here but still counted in totals`}>
        <GxPagedTable variant="sme"
          rows={data?.topBrands || []}
          rowKey={(b) => b.brandId}
          emptyText="No SME brand revenue in this window."
          headerGroups={[periodLeadGroup(2, proshipLabel), ...GROUPED_METRIC_GROUPS, { label: '', span: 1 }]}
          columns={[
            { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <Box component="span" sx={{ fontWeight: 600 }}>{b.brandName}</Box> },
            { key: 'poc', header: 'Sales POC', title: POC_TITLE, sortValue: (b) => b.poc, cell: (b) => b.poc },
            ...groupedMetricColumns((b) => b),
            { key: 'lastShip', header: 'Last shipment', title: LAST_SHIP_TITLE, align: 'right', sortValue: (b) => b.daysSinceShipment ?? -1, cell: (b) => daysAgoLabel(b.daysSinceShipment) },
          ]}
        />
      </Collapsible>
    </Box>
  )
}
